/**
 * @file offline/syncEngine.js
 * @description The sync engine handles bidirectional data sync between IndexedDB
 * and the server. It pushes pending local changes when online and pulls fresh
 * data from the server to keep the local cache up to date.
 *
 * Strategies:
 * - On save: always write to IndexedDB first (instant), then try server
 * - On reconnect: push all pending changes, then pull latest
 * - Conflict resolution: server wins, but local changes preserved in conflict log
 * - Photo sync: upload one at a time to avoid flooding
 * - Exponential backoff on failures
 * - Wave 2 (spec v2 §2.9): the queue meets the 24-hour session deadline. A
 *   401 TOKEN_EXPIRED / SESSION_REVOKED (or a refresh failing REFRESH_EXPIRED)
 *   during a flush STOPS the retry loop, keeps every queued item, and shows
 *   "Sign in again to finish syncing (N items)"; after re-login the flush
 *   resumes with the same entries. A queued item is NEVER dropped on
 *   401 / 403 / 503, and the local Dexie shelf is never wiped by an auth failure.
 */
import offlineStore from "./offlineStore";
import db from "./offlineDb";

/** Thrown by authFetchJSON when the session is gone: the flush must stop, nothing may be dropped. */
export class SyncAuthError extends Error {
  constructor(code) {
    super("Sign in again to finish syncing");
    this.name = "SyncAuthError";
    this.code = code || "TOKEN_EXPIRED";
  }
}
/** Thrown when Core / the API is busy (503 / 429): keep the item, retry later. */
export class SyncUnavailableError extends Error {
  constructor(status) {
    super("Sign-in service is busy");
    this.name = "SyncUnavailableError";
    this.status = status;
  }
}

/** Sync engine state */
let syncState = {
  isSyncing: false,
  isOnline: typeof navigator !== "undefined" ? navigator.onLine : true,
  pendingCount: 0,
  syncProgress: { current: 0, total: 0 },
  lastSyncTime: null,
  error: null,
  retryCount: 0,
  maxRetries: 5,
  listeners: new Set(),
  // Wave 2 §2.9
  authRequired: false,      // a flush hit the deadline / a revoke: wait for re-login, keep the queue
  sessionExpiresAt: null,   // epoch seconds from /api/auth/me session.expiresAt — drives the T+23h banner
};

/** Notify all listeners of state changes */
function notifyListeners() {
  syncState.listeners.forEach((fn) => {
    try { fn({ ...syncState }); } catch (e) { /* listener errors should not crash sync */ }
  });
}

/** Subscribe to sync state changes */
export function onSyncStateChange(fn) {
  syncState.listeners.add(fn);
  // Immediately call with current state
  fn({ ...syncState });
  return () => syncState.listeners.delete(fn);
}

/** Get current sync state */
export function getSyncState() {
  return { ...syncState };
}

/** Helper: make an authenticated fetch (uses cookies) */
async function authFetchJSON(url, options = {}) {
  const opts = {
    ...options,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(options.headers || {}),
    },
  };

  // Remove Content-Type for FormData
  if (options.body instanceof FormData) {
    delete opts.headers["Content-Type"];
  }

  let response = await fetch(url, opts);

  // Auto-refresh token on 401. Wave 2 §2.9: if the refresh is refused
  // (REFRESH_EXPIRED / SESSION_REVOKED — the 24-hour deadline or a sign-out)
  // the session is gone: raise SyncAuthError so the flush stops and keeps
  // every item, instead of the caller treating a 401 as a failed item.
  if (response.status === 401) {
    const refreshRes = await fetch("/api/auth/refresh", {
      method: "POST",
      credentials: "include",
    });
    if (refreshRes.ok) {
      response = await fetch(url, opts);
      if (response.status === 401) throw new SyncAuthError("TOKEN_EXPIRED");
    } else {
      let code = "REFRESH_EXPIRED";
      try { code = (await refreshRes.json()).code || code; } catch (e) { /* body optional */ }
      throw new SyncAuthError(code);
    }
  }
  // A 403 on a sync write is an account/role question, never a reason to drop work.
  if (response.status === 403) throw new SyncAuthError("FORBIDDEN");
  // Core / API busy: keep the item, come back later.
  if (response.status === 503 || response.status === 429) throw new SyncUnavailableError(response.status);

  return response;
}

/**
 * Wave 2 §2.9: the app just signed in (or confirmed a session). Clears the
 * "sign in again" hold, remembers the deadline for the banner, and resumes
 * the flush with the SAME queued entries.
 * @param {{expiresAt?: number}|null} session  from /api/auth/me or /login
 */
export function notifySignedIn(session) {
  const hadHold = syncState.authRequired;
  syncState.authRequired = false;
  syncState.sessionExpiresAt = session && Number.isFinite(session.expiresAt) ? session.expiresAt : null;
  if (hadHold) syncState.error = null;
  notifyListeners();
  if (hadHold && syncState.isOnline) setTimeout(() => syncAll(), 500);
}

/** Wave 2 §2.9: signed out on purpose — the queue stays on the shelf, untouched. */
export function notifySignedOut() {
  syncState.sessionExpiresAt = null;
  notifyListeners();
}

/** Calculate exponential backoff delay */
function getBackoffDelay(retryCount) {
  return Math.min(1000 * Math.pow(2, retryCount), 30000); // Max 30 seconds
}

// ==================== SYNC OPERATIONS ====================

/** Sync a batch of responses to the server */
async function syncResponses(operation) {
  const { inspectionId, responses } = operation.data;

  // Strip offline metadata before sending
  const cleanResponses = responses.map((r) => {
    const { _syncStatus, _lastModified, _serverId, blob, thumbnail, localUrl, ...clean } = r;
    return clean;
  });

  const res = await authFetchJSON("/api/inspections/" + inspectionId + "/responses", {
    method: "PUT",
    body: JSON.stringify({ responses: cleanResponses }),
  });

  if (!res.ok) {
    const errData = await res.json().catch(() => ({}));
    throw new Error(errData.error?.message || "Failed to sync responses");
  }

  const data = await res.json();

  // Update local records with server state
  if (data.data?.responses) {
    for (const serverResp of data.data.responses) {
      await offlineStore.markRecordSynced("responses", serverResp.id, serverResp.id);
    }
  }
}

/** Upload a single photo to the server */
async function syncPhoto(operation) {
  const { id, inspectionId, responseId } = operation.data;
  const photo = await db.photos.get(id);

  if (!photo || !photo.blob) {
    // Photo was deleted locally or has no blob data, skip
    return;
  }

  const formData = new FormData();

  // Convert blob back to File
  let file;
  if (photo.blob instanceof Blob) {
    file = new File([photo.blob], photo.filename || "photo.jpg", { type: photo.mimeType || "image/jpeg" });
  } else {
    // If stored as ArrayBuffer or base64
    const blob = new Blob([photo.blob], { type: photo.mimeType || "image/jpeg" });
    file = new File([blob], photo.filename || "photo.jpg", { type: photo.mimeType || "image/jpeg" });
  }

  formData.append("photos", file);
  if (responseId) {
    formData.append("responseId", responseId);
  }

  const res = await authFetchJSON("/api/inspections/" + inspectionId + "/photos", {
    method: "POST",
    body: formData,
  });

  if (!res.ok) {
    throw new Error("Photo upload failed");
  }

  const data = await res.json();
  if (data.data?.photos && data.data.photos.length > 0) {
    const serverPhoto = data.data.photos[0];
    // Update local record with server data
    await db.photos.update(id, {
      _serverId: serverPhoto.id,
      _syncStatus: "synced",
      url: serverPhoto.url,
      thumbnailPath: serverPhoto.thumbnailPath,
    });
  }
}

/** Delete a photo on the server */
async function syncPhotoDelete(operation) {
  const { serverId, inspectionId } = operation.data;
  if (!serverId) return; // Never made it to server

  const res = await authFetchJSON(
    "/api/inspections/" + inspectionId + "/photos/" + serverId,
    { method: "DELETE" }
  );

  // 404 is OK — already deleted on server
  if (!res.ok && res.status !== 404) {
    throw new Error("Photo delete failed");
  }
}

/** Sync an inspection update to the server */
async function syncInspection(operation) {
  const { _syncStatus, _lastModified, _serverId, ...cleanData } = operation.data;
  const serverId = _serverId || cleanData.id;

  if (operation.operation === "update" && serverId) {
    const res = await authFetchJSON("/api/inspections/" + serverId, {
      method: "PUT",
      body: JSON.stringify(cleanData),
    });
    if (!res.ok) {
      const errData = await res.json().catch(() => ({}));
      throw new Error(errData.error?.message || "Failed to sync inspection");
    }
    await offlineStore.markRecordSynced("inspections", cleanData.id, serverId);
  }
}

// CRM (Customer Butler) table -> REST resource path.
const CRM_ENDPOINTS = {
  customers: "/api/customer/customers",
  properties: "/api/customer/properties",
  contacts: "/api/customer/contacts",
  agents: "/api/customer/agents",
  tasks: "/api/customer/tasks",
};

/**
 * Sync a CRM create/update. On create, swap the local temp (client-id) record
 * for the server's record so the server id becomes canonical and the row never
 * duplicates when the next pull arrives.
 */
async function syncCrm(operation) {
  const path = CRM_ENDPOINTS[operation.table];
  if (!path) return;
  const { _syncStatus, _lastModified, _serverId, ...clean } = operation.data;

  if (operation.operation === "create") {
    const res = await authFetchJSON(path, { method: "POST", body: JSON.stringify(clean) });
    if (!res.ok) {
      const errData = await res.json().catch(() => ({}));
      throw new Error(errData.error?.message || ("Failed to sync " + operation.table));
    }
    const created = (await res.json().catch(() => ({}))).data;
    await db[operation.table].delete(operation.recordId);
    if (created && created.id) {
      await db[operation.table].put({
        ...created, _serverId: created.id, _syncStatus: "synced",
        _lastModified: new Date().toISOString(),
      });
    }
  } else if (operation.operation === "update") {
    const serverId = _serverId || clean.id;
    const res = await authFetchJSON(path + "/" + serverId, { method: "PUT", body: JSON.stringify(clean) });
    if (!res.ok) {
      const errData = await res.json().catch(() => ({}));
      throw new Error(errData.error?.message || ("Failed to sync " + operation.table));
    }
    await offlineStore.markRecordSynced(operation.table, clean.id, serverId);
  }
}

// ==================== MAIN SYNC LOOP ====================

/** Process all pending sync operations */
export async function syncAll() {
  if (syncState.isSyncing || !syncState.isOnline) return;
  // Wave 2 §2.9: a dead session holds the queue until the next sign-in.
  if (syncState.authRequired) return;

  syncState.isSyncing = true;
  syncState.error = null;
  notifyListeners();

  try {
    const pending = await offlineStore.getPendingOperations();
    syncState.syncProgress = { current: 0, total: pending.length };
    syncState.pendingCount = pending.length;
    notifyListeners();

    if (pending.length === 0) {
      syncState.isSyncing = false;
      syncState.lastSyncTime = new Date().toISOString();
      notifyListeners();
      return;
    }

    for (let i = 0; i < pending.length; i++) {
      const op = pending[i];
      syncState.syncProgress = { current: i + 1, total: pending.length };
      notifyListeners();

      try {
        switch (op.table) {
          case "responses":
            await syncResponses(op);
            break;
          case "photos":
            if (op.operation === "upload") {
              await syncPhoto(op);
            } else if (op.operation === "delete") {
              await syncPhotoDelete(op);
            }
            break;
          case "inspections":
            await syncInspection(op);
            break;
          case "customers":
          case "properties":
          case "contacts":
          case "agents":
          case "tasks":
            await syncCrm(op);
            break;
          default:
            break;
        }
        await offlineStore.markSynced(op.id);
      } catch (err) {
        // Wave 2 §2.9: an auth or availability failure stops THIS flush and
        // keeps every remaining item pending — never markFailed, never a
        // conflict entry, never a deletion. The shelf stays whole.
        if (err && (err.name === "SyncAuthError" || err.name === "SyncUnavailableError")) {
          const remaining = pending.length - i;
          const items = remaining + " item" + (remaining === 1 ? "" : "s");
          if (err.name === "SyncAuthError") {
            syncState.authRequired = true;
            syncState.error = "Sign in again to finish syncing (" + items + ")";
          } else {
            syncState.error = "Sign-in service is busy \u2014 " + items + " still queued, will retry";
            syncState.retryCount++;
            if (syncState.retryCount <= syncState.maxRetries) {
              setTimeout(() => syncAll(), getBackoffDelay(syncState.retryCount));
            }
          }
          break;
        }
        console.error("Sync failed for operation:", op.table, op.operation, err);
        await offlineStore.markFailed(op.id, err.message);

        // For photos, skip and continue — do not block the entire sync
        if (op.table === "photos") {
          continue;
        }

        // For other operations, log conflict and continue
        await offlineStore.logConflict(op.table, op.recordId, op.data, null);
      }
    }

    if (!syncState.authRequired && !syncState.error) syncState.retryCount = 0;
    syncState.lastSyncTime = new Date().toISOString();

    // Update pending count after sync
    syncState.pendingCount = await offlineStore.getPendingCount();

    // Cleanup old completed entries
    await offlineStore.cleanupSyncQueue();
  } catch (err) {
    console.error("Sync engine error:", err);
    syncState.error = err.message;
    syncState.retryCount++;

    // Schedule retry with exponential backoff
    if (syncState.retryCount <= syncState.maxRetries) {
      const delay = getBackoffDelay(syncState.retryCount);
      setTimeout(() => syncAll(), delay);
    }
  } finally {
    syncState.isSyncing = false;
    notifyListeners();
  }
}

// ==================== PULL FROM SERVER ====================

/** Pull latest data from server and update local cache */
export async function pullFromServer(authFetch) {
  if (!syncState.isOnline) return;

  try {
    // Pull dashboard data
    const dashRes = await authFetch("/api/dashboard");
    if (dashRes.ok) {
      const dashData = await dashRes.json();
      await offlineStore.cacheDashboard(dashData.data || dashData);
    }

    // Pull templates list and cache them
    const templatesRes = await authFetch("/api/templates");
    if (templatesRes.ok) {
      const templatesData = await templatesRes.json();
      const templates = templatesData.data?.templates || templatesData.templates || [];
      for (const template of templates) {
        // Fetch full template with sections/items
        try {
          const fullRes = await authFetch("/api/templates/" + template.id);
          if (fullRes.ok) {
            const fullData = await fullRes.json();
            const tmpl = fullData.data?.template || fullData.template;
            const sections = fullData.data?.sections || fullData.sections || [];
            if (tmpl) {
              await offlineStore.cacheTemplate(tmpl, sections);
            }
          }
        } catch (e) {
          // Non-critical, skip
        }
      }
    }

    // Pull inspections list
    const inspRes = await authFetch("/api/inspections?limit=100");
    if (inspRes.ok) {
      const inspData = await inspRes.json();
      const inspections = inspData.data?.inspections || inspData.inspections || [];
      for (const insp of inspections) {
        // Only update if not locally modified
        const local = await offlineStore.getInspection(insp.id);
        if (!local || local._syncStatus === "synced") {
          await db.inspections.put({
            ...insp,
            _serverId: insp.id,
            _syncStatus: "synced",
            _lastModified: new Date().toISOString(),
          });
        }
      }
    }
  } catch (err) {
    console.error("Pull from server failed:", err);
    // Not critical — we have cached data
  }
}

/** Download a specific inspection for offline use */
export async function downloadForOffline(inspectionId, authFetch) {
  try {
    const res = await authFetch("/api/inspections/" + inspectionId);
    if (!res.ok) throw new Error("Failed to fetch inspection");

    const data = await res.json();
    const inspection = data.data?.inspection || data.inspection;
    const responses = data.data?.responses || data.responses || [];
    const photos = data.data?.photos || data.photos || [];

    // Cache inspection and responses
    await offlineStore.cacheFullInspection(inspection, responses, photos);

    // Cache the template if needed
    if (inspection.templateId) {
      const cached = await offlineStore.getTemplate(inspection.templateId);
      if (!cached) {
        const tmplRes = await authFetch("/api/templates/" + inspection.templateId);
        if (tmplRes.ok) {
          const tmplData = await tmplRes.json();
          const template = tmplData.data?.template || tmplData.template;
          const sections = tmplData.data?.sections || tmplData.sections || [];
          if (template) {
            await offlineStore.cacheTemplate(template, sections);
          }
        }
      }
    }

    // Download and cache photo blobs
    for (const photo of photos) {
      try {
        const photoUrl = photo.url || photo.thumbnailPath;
        if (photoUrl) {
          const photoRes = await fetch(photoUrl, { credentials: "include" });
          if (photoRes.ok) {
            const blob = await photoRes.blob();
            await db.photos.update(photo.id, { blob });
          }
        }
      } catch (e) {
        // Photo download failed, skip — metadata is still cached
      }
    }

    return true;
  } catch (err) {
    console.error("Download for offline failed:", err);
    throw err;
  }
}

// ==================== ONLINE/OFFLINE LISTENERS ====================

/** Initialize the sync engine — call once on app start */
export function initSyncEngine() {
  // Update online state
  const updateOnlineStatus = () => {
    const wasOffline = !syncState.isOnline;
    syncState.isOnline = navigator.onLine;
    notifyListeners();

    // If we just came back online, trigger a sync
    if (wasOffline && syncState.isOnline) {
      setTimeout(() => syncAll(), 1000); // Small delay to let connection stabilize
    }
  };

  window.addEventListener("online", updateOnlineStatus);
  window.addEventListener("offline", updateOnlineStatus);

  // Check pending count on init
  offlineStore.getPendingCount().then((count) => {
    syncState.pendingCount = count;
    notifyListeners();
  });

  // If online, do an initial sync of any pending items
  if (navigator.onLine) {
    setTimeout(() => syncAll(), 2000);
  }

  // Periodic sync check (every 60 seconds)
  setInterval(async () => {
    if (syncState.isOnline && !syncState.isSyncing) {
      const count = await offlineStore.getPendingCount();
      if (count > 0) {
        syncAll();
      }
    }
  }, 60000);

  return () => {
    window.removeEventListener("online", updateOnlineStatus);
    window.removeEventListener("offline", updateOnlineStatus);
  };
}

export default {
  syncAll,
  pullFromServer,
  downloadForOffline,
  initSyncEngine,
  onSyncStateChange,
  getSyncState,
  notifySignedIn,
  notifySignedOut,
};
